/* PublicUserProfile — Instagram/Tinder-style public card of another user.
   ───────────────────────────────────────────────────────────────────────
   Route: /app/u/:userId
   API:   GET /api/users/:user_id → enriched with orio_score + dogs + active story

   Layout (mobile-first):
   ┌──────────────────────────────┐
   │ ← back        ⋯ report       │  header
   │                              │
   │      [ big avatar ]          │  tap → StoriesMine viewer of that user
   │      story-ring if active    │
   │                              │
   │   Marco · 32  · Milano       │
   │   [ PRO ] [ Volontario ]     │
   │                              │
   │   ─ bio ─                    │
   │                              │
   │  #interests #tags            │
   │                              │
   │  🐾 I miei cani (3)          │  horizontal scroller
   │  ┌───┐┌───┐┌───┐             │
   │  │ 1 ││ 2 ││ 3 │             │
   │  └───┘└───┘└───┘             │
   │                              │
   │  ORIO score                  │
   │  ┌──────┬──────┬──────┐      │
   │  │ 12   │ 4    │ 320  │      │
   │  │miss  │hazrd │karma │      │
   │  └──────┴──────┴──────┘      │
   │                              │
   │  [ Messaggia ]  [ Saluta ]   │  CTA (auth-only)
   └──────────────────────────────┘
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import {
  ArrowLeft, Award, Building2, Camera, Crown, Flag, GripVertical, Heart, MapPin, MessageCircle,
  MoreVertical, Pencil, Plus, ShieldAlert, ShieldCheck, Sparkles, Trash2, Users, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";

const ORANGE = "#FF7A1A";
const CREAM = "#FBF7F1";
const INK = "#1A1A1A";

export default function PublicUserProfile() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waving, setWaving] = useState(false);
  const [showReport, setShowReport] = useState(false);
  // Photo carousel + editors — only surfaced when `isSelf`.
  const [photoIdx, setPhotoIdx] = useState(0);
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState("");
  const [savingBio, setSavingBio] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Drag-to-reorder modal — opens a full-screen sheet with all photos in
  // a grid; user drags to rearrange, first slot becomes the cover.
  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${userId}`);
        if (!cancelled) setP(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Profilo non disponibile");
        nav(-1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, nav]);

  const isSelf = me?.id === userId;

  const wave = async () => {
    if (waving) return;
    setWaving(true);
    try {
      await api.post("/presence/greet", { to_user_id: userId });
      toast.success("👋 Saluto inviato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salutare");
    } finally {
      setWaving(false);
    }
  };

  // ── Photo carousel — computed list. Falls back to primary photo when
  //    the user only has the classic single-avatar shape.
  const photos = useMemo(() => {
    if (!p) return [];
    const arr = (p.photos && p.photos.length > 0) ? p.photos : (p.photo_url ? [p.photo_url] : []);
    return arr;
  }, [p]);

  // Reset the carousel index when the user's photo list changes.
  useEffect(() => {
    if (photoIdx >= photos.length) setPhotoIdx(0);
  }, [photos, photoIdx]);

  // ── Add a new photo to my own profile. Goes through the multipart
  //    POST /users/me/photos endpoint which writes to Object Storage.
  const uploadPhoto = async (file) => {
    if (!file || uploadingPhoto) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/me/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setP((prev) => prev ? { ...prev, photos: data.photos, photo_url: data.photo_url || prev.photo_url } : prev);
      hapticTap();
      toast.success("Foto aggiunta");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deletePhoto = async (idx) => {
    if (!confirm("Rimuovere questa foto dal profilo?")) return;
    try {
      const { data } = await api.delete(`/users/me/photos/${idx}`);
      setP((prev) => prev ? { ...prev, photos: data.photos, photo_url: data.photo_url ?? prev.photo_url } : prev);
      setPhotoIdx((i) => Math.max(0, Math.min(i, (data.photos?.length || 1) - 1)));
      toast.success("Foto rimossa");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile rimuovere");
    }
  };

  const startEditingBio = () => {
    setBioDraft(p?.bio || "");
    setEditingBio(true);
  };
  const saveBio = async () => {
    setSavingBio(true);
    try {
      const { data } = await api.put("/users/me/bio", { bio: bioDraft });
      setP((prev) => prev ? { ...prev, bio: data.bio } : prev);
      setEditingBio(false);
      toast.success("Bio aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare");
    } finally {
      setSavingBio(false);
    }
  };

  // ── Drag-to-reorder: open the sheet + persist the new order.
  const openReorder = () => {
    setReorderList([...photos]);
    setReorderOpen(true);
  };
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const { data } = await api.put("/users/me/photos/reorder", { photos: reorderList });
      setP((prev) => prev ? { ...prev, photos: data.photos, photo_url: data.photo_url ?? prev.photo_url } : prev);
      setReorderOpen(false);
      setPhotoIdx(0);
      hapticTap();
      toast.success("Ordine foto salvato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare");
    } finally {
      setSavingOrder(false);
    }
  };

  const openStories = () => {
    if (!p?.has_active_story) return;
    // Deep-link into the Stories viewer, starting from this user's stories.
    nav(`/app/match?tab=stories&user=${p.id}`);
  };

  if (loading) return <ProfileSkeleton onBack={() => nav(-1)} />;
  if (!p) return null;

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+120px)] md:pb-10"
      style={{ background: CREAM, color: INK }}
      data-testid="public-user-profile"
    >
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-md bg-[#FBF7F1]/85 border-b border-[#F0EBE3]">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => nav(-1)}
            data-testid="public-profile-back"
            className="h-10 w-10 rounded-full flex items-center justify-center bg-white/70 border border-[#F0EBE3]"
            aria-label="Indietro"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="text-[13px] font-semibold tracking-wide">Profilo</div>
          {!isSelf && (
            <button
              onClick={() => setShowReport(true)}
              data-testid="public-profile-more"
              className="h-10 w-10 rounded-full flex items-center justify-center bg-white/70 border border-[#F0EBE3]"
              aria-label="Segnala"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          )}
          {isSelf && <span className="w-10" aria-hidden />}
        </div>
      </div>

      {/* Hero — Tinder-style photo carousel + overlay identity block. */}
      <div className="max-w-md mx-auto px-3 pt-3 pb-4">
        <div
          className="relative w-full rounded-3xl overflow-hidden bg-black"
          style={{ aspectRatio: "3 / 4", boxShadow: "0 30px 60px -20px rgba(0,0,0,0.28)" }}
          data-testid="public-profile-carousel"
        >
          {/* Tap-zones for prev/next photo (Tinder-style) */}
          {photos.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Foto precedente"
                onClick={() => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length)}
                className="absolute inset-y-0 left-0 w-1/3 z-10"
                data-testid="carousel-prev"
              />
              <button
                type="button"
                aria-label="Foto successiva"
                onClick={() => setPhotoIdx((i) => (i + 1) % photos.length)}
                className="absolute inset-y-0 right-0 w-1/3 z-10"
                data-testid="carousel-next"
              />
            </>
          )}
          {/* Photo */}
          {photos.length > 0 ? (
            <img
              src={photos[photoIdx]}
              alt={p.name || ""}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
              <span className="font-display text-white text-8xl">
                {(p.name || "?").charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          {/* Story ring overlay — tap on the photo (avoid tap-zones) to open stories */}
          {p.has_active_story && (
            <button
              onClick={openStories}
              data-testid="public-profile-open-stories"
              className="absolute top-3 left-3 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white active:scale-95"
              style={{
                background: "linear-gradient(135deg,#FF7A1A,#E9542A)",
                boxShadow: "0 10px 20px -6px rgba(233,84,42,0.55)",
              }}
            >
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> Storia in corso
            </button>
          )}
          {/* Segmented dots at the top */}
          {photos.length > 1 && (
            <div className="absolute top-2.5 inset-x-2.5 z-20 flex gap-1 pointer-events-none">
              {photos.map((_, i) => (
                <span
                  key={i}
                  className="h-1 flex-1 rounded-full"
                  style={{ background: i === photoIdx ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)" }}
                />
              ))}
            </div>
          )}
          {/* Bottom gradient for legibility */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 46%, rgba(0,0,0,0.35) 76%, rgba(0,0,0,0.82) 100%)" }}
          />
          {/* Identity block */}
          <div className="absolute inset-x-4 bottom-4 z-10 pointer-events-none">
            <div className="text-white font-display text-3xl leading-tight" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}>
              {p.name || "Utente ORIO"}
              {p.age != null && <span className="text-white/85 font-normal ml-2">· {p.age}</span>}
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap text-white/90 text-[13px]">
              {p.city && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.city}</span>
              )}
              {p.member_since && (
                <span className="text-white/70 text-[11px]">Su ORIO da {new Date(p.member_since).toLocaleDateString("it-IT", { month: "short", year: "numeric" })}</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 pointer-events-auto">
              {p.is_pro && <MiniBadge label="PRO" bg="linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" />}
              {p.is_activist && <MiniBadge label="Volontario" bg="rgba(45,186,96,0.8)" />}
              {p.is_shelter && <MiniBadge label={p.shelter_name || "Rifugio"} bg="rgba(91,158,224,0.85)" />}
              <MiniBadge label={p.orio_score?.level || "Cittadino"} bg="rgba(255,122,26,0.85)" />
            </div>
          </div>
          {/* Self-only photo controls: add + delete current + reorder */}
          {isSelf && (
            <div className="absolute bottom-4 right-4 z-30 flex flex-col gap-2">
              {photos.length >= 2 && (
                <button
                  onClick={openReorder}
                  aria-label="Riordina foto"
                  data-testid="carousel-reorder"
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white active:scale-95"
                  style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }}
                >
                  <GripVertical className="h-4 w-4" strokeWidth={2.4} />
                </button>
              )}
              {photos.length > 0 && (
                <button
                  onClick={() => deletePhoto(photoIdx)}
                  aria-label="Rimuovi foto"
                  data-testid="carousel-delete"
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white active:scale-95"
                  style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              {photos.length < 6 && (
                <button
                  onClick={() => fileRef.current?.click()}
                  aria-label="Aggiungi foto"
                  data-testid="carousel-add"
                  disabled={uploadingPhoto}
                  className="h-11 w-11 rounded-full flex items-center justify-center text-white active:scale-95"
                  style={{
                    background: "linear-gradient(135deg,#FF7A1A,#F4B83C)",
                    boxShadow: "0 12px 24px -6px rgba(255,122,26,0.55)",
                    opacity: uploadingPhoto ? 0.6 : 1,
                  }}
                >
                  {uploadingPhoto ? "…" : <Plus className="h-5 w-5" strokeWidth={2.4} />}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bio — inline editor when it's my own profile */}
      {(p.bio || isSelf) && (
        <div className="max-w-md mx-auto px-5 pb-3">
          {editingBio ? (
            <div className="rounded-2xl bg-white border border-[#F0EBE3] p-3">
              <textarea
                value={bioDraft}
                onChange={(e) => setBioDraft(e.target.value.slice(0, 280))}
                rows={4}
                placeholder="Racconta chi sei e cosa ami del tuo cane (max 280 caratteri)"
                data-testid="public-profile-bio-textarea"
                className="w-full text-[14px] leading-relaxed text-[#1A1A1A] bg-transparent outline-none resize-none"
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] text-[#9A9A9A]">{bioDraft.length}/280</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingBio(false)}
                    className="text-[13px] px-3 py-1.5 rounded-full text-[#7A7A7A]"
                    data-testid="public-profile-bio-cancel"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={saveBio}
                    disabled={savingBio}
                    className="text-[13px] px-3.5 py-1.5 rounded-full font-semibold text-white"
                    data-testid="public-profile-bio-save"
                    style={{
                      background: "linear-gradient(135deg,#FF7A1A,#F4B83C)",
                      opacity: savingBio ? 0.6 : 1,
                    }}
                  >
                    {savingBio ? "Salvo…" : "Salva"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative rounded-2xl bg-white border border-[#F0EBE3] px-4 py-3 text-[14px] leading-relaxed text-[#333]">
              {p.bio || <span className="text-[#9A9A9A] italic">Aggiungi una bio per farti conoscere…</span>}
              {isSelf && (
                <button
                  onClick={startEditingBio}
                  aria-label="Modifica bio"
                  data-testid="public-profile-bio-edit"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full flex items-center justify-center text-[#B5601D]"
                  style={{ background: "rgba(255,122,26,0.12)" }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Interests */}
      {p.interests?.length > 0 && (
        <div className="max-w-md mx-auto px-5 pb-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7A7A] mb-2">Interessi</div>
          <div className="flex flex-wrap gap-2">
            {p.interests.map((tag) => (
              <span key={tag} className="text-[12px] px-3 py-1 rounded-full bg-white border border-[#E5DECF] text-[#3A3A3A]">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dogs strip */}
      {p.dogs?.length > 0 && (
        <div className="max-w-md mx-auto pl-5 pr-2 pb-5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7A7A] mb-2 inline-flex items-center gap-1.5">
            🐾 I suoi cani ({p.dogs.length})
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pr-3">
            {p.dogs.map((d) => (
              <DogMini key={d.id} dog={d} />
            ))}
          </div>
        </div>
      )}

      {/* ORIO score */}
      <div className="max-w-md mx-auto px-5 pb-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7A7A] mb-2 inline-flex items-center gap-1.5">
          <Award className="h-3 w-3" /> ORIO Score
        </div>
        <div className="grid grid-cols-4 gap-2">
          <ScoreTile value={p.orio_score?.missions_completed || 0} label="Missioni" icon={Users} tint="#2DBA60" />
          <ScoreTile value={p.orio_score?.hazards_reported || 0} label="Pericoli" icon={ShieldAlert} tint="#E23B3B" />
          <ScoreTile value={p.orio_score?.walks_completed || 0} label="Passeggiate" icon={Heart} tint="#5B9EE0" />
          <ScoreTile value={p.orio_score?.karma_balance || 0} label="Karma" icon={Sparkles} tint="#FF7A1A" />
        </div>
      </div>

      {/* CTA */}
      {!isSelf && (
        <div className="max-w-md mx-auto px-5 pb-6 flex gap-2">
          <Link
            to={`/app/messaggi?open=${p.id}`}
            data-testid="public-profile-msg"
            className="flex-1 h-12 rounded-2xl inline-flex items-center justify-center gap-2 text-[14px] font-semibold text-white"
            style={{ background: "linear-gradient(135deg, #1A1A1A 0%, #3A3A3A 100%)" }}
          >
            <MessageCircle className="h-4 w-4" /> Messaggia
          </Link>
          <button
            onClick={() => { hapticTap(); wave(); }}
            disabled={waving}
            data-testid="public-profile-wave"
            className="flex-1 h-12 rounded-2xl inline-flex items-center justify-center gap-2 text-[14px] font-semibold text-white disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}
          >
            👋 Saluta
          </button>
        </div>
      )}

      {showReport && (
        <ReportSheet userId={p.id} onClose={() => setShowReport(false)} />
      )}

      {reorderOpen && (
        <PhotoReorderSheet
          list={reorderList}
          setList={setReorderList}
          saving={savingOrder}
          onClose={() => setReorderOpen(false)}
          onSave={saveOrder}
        />
      )}
    </div>
  );
}

/* ── Photo drag-to-reorder full-screen sheet ─────────────────────────── */
function PhotoReorderSheet({ list, setList, saving, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center" data-testid="photo-reorder-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
      <div
        className="relative w-full sm:max-w-md mx-3 sm:mx-0 rounded-t-[28px] sm:rounded-[28px] bg-[#FBF7F1] border border-[#F0EBE3] overflow-hidden"
        style={{ maxHeight: "92dvh" }}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#B75E2A]">
              Ordina le foto
            </div>
            <h3 className="mt-0.5 font-display text-[20px] leading-tight text-[#1A1A1A]" style={{ fontWeight: 700 }}>
              La prima è la copertina
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Chiudi"
            data-testid="reorder-close"
            className="h-10 w-10 rounded-full flex items-center justify-center text-[#7A7A7A]"
            style={{ background: "rgba(0,0,0,0.06)" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 pb-2 text-[12px] text-[#6B6B6B]">
          Trascina in alto la tua foto migliore — sarà quella che vedranno per prima.
        </div>
        <Reorder.Group
          axis="y"
          values={list}
          onReorder={setList}
          className="px-5 py-3 space-y-2.5 overflow-y-auto"
          style={{ maxHeight: "64vh" }}
        >
          {list.map((url, i) => (
            <Reorder.Item
              key={url}
              value={url}
              className="flex items-center gap-3 p-2 rounded-2xl bg-white border border-[#F0EBE3] cursor-grab active:cursor-grabbing"
              whileDrag={{ scale: 1.03, boxShadow: "0 20px 40px -12px rgba(0,0,0,0.30)" }}
              data-testid={`reorder-item-${i}`}
            >
              <div className="text-[#B5601D] shrink-0">
                <GripVertical className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div className="relative h-16 w-16 rounded-xl overflow-hidden bg-[#EFE9DC] shrink-0">
                <img src={url} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <div
                    className="absolute inset-x-0 bottom-0 text-white text-[9px] font-bold uppercase tracking-widest text-center py-0.5"
                    style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}
                  >
                    Copertina
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1A1A]">
                  Foto {i + 1}
                </div>
                <div className="text-[11px] text-[#7A7A7A]">
                  Trascina per riordinare
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
        <div className="px-5 pb-5 pt-3 flex gap-2 border-t border-[#F0EBE3]">
          <button
            onClick={onClose}
            className="flex-1 h-12 rounded-2xl inline-flex items-center justify-center text-[14px] font-semibold text-[#3A3A3A] bg-white border border-[#F0EBE3]"
            data-testid="reorder-cancel"
          >
            Annulla
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="reorder-save"
            className="flex-1 h-12 rounded-2xl inline-flex items-center justify-center text-[14px] font-semibold text-white disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
              boxShadow: "0 12px 24px -8px rgba(255,122,26,0.55)",
            }}
          >
            {saving ? "Salvo…" : "Salva ordine"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Small building blocks ─────────────────────────────────────────── */

function Badge({ icon: Icon, label, bg, fg }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
      style={{ background: bg, color: fg }}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

/* MiniBadge — for the identity block over the carousel photo. Text-only
   pill on a translucent background so it stays legible on any photo. */
function MiniBadge({ label, bg }) {
  return (
    <span
      className="inline-block text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{ background: bg, backdropFilter: "blur(6px)" }}
    >
      {label}
    </span>
  );
}

function DogMini({ dog }) {
  const age = dog.birth_date ? computeAgeYears(dog.birth_date) : null;
  return (
    <Link
      to={`/passport/${dog.id}`}
      className="shrink-0 snap-start w-[128px] rounded-2xl overflow-hidden bg-white border border-[#F0EBE3] active:scale-[0.98] transition-transform"
    >
      <div className="h-[128px] w-full bg-[#F4EFE6] relative overflow-hidden">
        {dog.photo_url ? (
          <img src={dog.photo_url} alt={dog.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">🐕</div>
        )}
      </div>
      <div className="px-3 py-2">
        <div className="text-[13px] font-semibold leading-tight truncate">{dog.name || "—"}</div>
        <div className="text-[11px] text-[#7A7A7A] truncate">
          {[dog.breed, age ? `${age}a` : null].filter(Boolean).join(" · ")}
        </div>
      </div>
    </Link>
  );
}

function ScoreTile({ value, label, icon: Icon, tint }) {
  return (
    <div
      className="rounded-2xl px-2 py-3 border text-center bg-white"
      style={{ borderColor: "#F0EBE3" }}
    >
      <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: tint }} />
      <div className="text-lg font-bold font-display leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#7A7A7A] mt-1">{label}</div>
    </div>
  );
}

function ProfileSkeleton({ onBack }) {
  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <div className="sticky top-0 z-30 backdrop-blur-md bg-[#FBF7F1]/85 border-b border-[#F0EBE3]">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center">
          <button onClick={onBack} className="h-10 w-10 rounded-full flex items-center justify-center bg-white/70 border border-[#F0EBE3]">
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="max-w-md mx-auto px-5 py-10 space-y-4">
        <div className="mx-auto h-32 w-32 rounded-full bg-[#EFE9DE] animate-pulse" />
        <div className="h-6 w-40 mx-auto bg-[#EFE9DE] rounded animate-pulse" />
        <div className="h-4 w-24 mx-auto bg-[#EFE9DE] rounded animate-pulse" />
        <div className="h-24 w-full bg-[#EFE9DE] rounded-2xl animate-pulse" />
      </div>
    </div>
  );
}

/* ── Report sheet ──────────────────────────────────────────────────── */
function ReportSheet({ userId, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/reports", { target_user_id: userId, reason });
      toast.success("Segnalazione inviata — grazie.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio segnalazione");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[2500] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="relative w-full sm:max-w-md mx-3 rounded-t-[28px] sm:rounded-[28px] border border-[#F0EBE3] bg-white p-5 space-y-4"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-red-100 flex items-center justify-center text-red-500">
            <Flag className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-red-500">Segnala</div>
            <div className="font-display text-lg">Segnala questo profilo</div>
          </div>
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Motivo (es. contenuti non appropriati, comportamento sospetto)"
          rows={4}
          className="w-full rounded-2xl border border-[#E5DECF] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 rounded-full bg-[#F4EFE6] text-[#3A3A3A] text-[13px] font-semibold">
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="flex-1 h-11 rounded-full bg-red-500 text-white text-[13px] font-semibold disabled:opacity-60"
          >
            {busy ? "Invio…" : "Invia"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helper: age in whole years ─────────────────────────────────────── */
function computeAgeYears(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let y = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) y -= 1;
    return Math.max(0, y);
  } catch { return null; }
}
